import { Accordion, Anchor, Badge, Box, Card, Group, Loader, Stack, Text, TextInput, Title } from "@mantine/core";
import { useEffect, useMemo, useState } from "react";
import attributions from "../attribution/attributions.json";
// The GNU GPL text LGPL-3.0 section 4(b) wants shipped alongside the LGPL; served from here so nothing is fetched
// off another host to read it, and addressed rather than inlined like every other licence on the page.
import gplUrl from "../attribution/canonical/GPL-3.0.txt?url";
// PostgreSQL's own licence, which travels with the server PGlite carries and is in no package.json either.
import postgresUrl from "../attribution/canonical/PostgreSQL.txt?url";
// CPython's own licence, which travels with the standard library Pyodide carries and is in no package.json.
import pythonUrl from "../attribution/canonical/Python-2.0.txt?url";
// Unicode's own, which covers the character database /unicode is built from and is in no package.json either.
import unicodeUrl from "../attribution/canonical/Unicode-3.0.txt?url";
import { shuffle } from "./common/random";
import { IconSearch } from "./icons";

export default function Attributions() {
  const [query, setQuery] = useState("");
  const [opened, setOpened] = useState<string | null>(null);

  const needle = query.trim().toLowerCase();
  const matches = useMemo(
    () => needle ? packages.filter((p) => `${p.name} ${p.license}`.toLowerCase().includes(needle)) : packages,
    [needle],
  );

  return (
    <Stack gap="md">
      <Title order={1} size="h2" lh={1.15}>Attributions</Title>

      <Card withBorder shadow="sm" radius="md">
        <Stack gap="sm">
          <Text size="sm">
            utils+ is open source under the{" "}
            <Anchor href={`${REPO}/blob/main/LICENSE`} target="_blank" rel="noopener noreferrer" inherit>
              Apache License 2.0
            </Anchor>
            , and is built on the {packages.length}{" "}
            open source packages listed below. Every one of them is served from this site along with the rest of the
            page, so their licences travel with it — this page is where they are kept. Build tooling that never reaches
            your browser is not listed.
          </Text>
          <Text size="sm" c="dimmed">
            Licence texts are reproduced from the packages themselves. Where a package ships none, the canonical text
            for the licence it declares stands in and is marked as such.
          </Text>
        </Stack>
      </Card>

      <Card withBorder shadow="sm" radius="md">
        <Stack gap="sm">
          <Title order={4}>Licences in use</Title>
          <Group gap="xs">
            {/* tt="none" against Badge's uppercasing: an SPDX identifier is spelled the way SPDX spells it. */}
            {summary.map(([license, count]) => (
              <Badge key={license} variant="light" color="gray" size="lg" tt="none">
                {license} · {count}
              </Badge>
            ))}
          </Group>
        </Stack>
      </Card>

      <Card withBorder shadow="sm" radius="md">
        <Stack gap="sm">
          <TextInput
            label="Packages"
            description={`${matches.length} of ${packages.length} shown, in no particular order`}
            placeholder="Filter by name or licence"
            leftSection={<IconSearch size="1rem" stroke={1.5} />}
            value={query}
            onChange={(event) => setQuery(event.currentTarget.value)}
          />

          <Accordion variant="separated" value={opened} onChange={setOpened}>
            {matches.map((pkg) => (
              <Accordion.Item key={pkg.name} value={pkg.name}>
                <Accordion.Control>
                  <Group gap="xs" wrap="wrap">
                    <Text size="sm" ff="monospace">{pkg.name}</Text>
                    <Text size="sm" c="dimmed" ff="monospace">{pkg.version}</Text>
                    <Badge variant="light" color="gray" size="sm" tt="none">{pkg.license}</Badge>
                  </Group>
                </Accordion.Control>
                <Accordion.Panel>
                  {/* Only the open panel builds its text: 124 licences in the DOM at once is a page nobody is reading. */}
                  {opened === pkg.name && (
                    <Stack gap="xs">
                      <Group gap="sm">
                        {pkg.url && (
                          <Anchor href={pkg.url} target="_blank" rel="noopener noreferrer" size="sm">Source</Anchor>
                        )}
                        {pkg.publisher && <Text size="sm" c="dimmed">{pkg.publisher}</Text>}
                      </Group>
                      {pkg.reconstructed && (
                        <Text size="xs" c="dimmed">
                          This package ships no licence file; below is the canonical {pkg.license} text.
                        </Text>
                      )}
                      <Licence url={LICENCES[pkg.file]} />
                    </Stack>
                  )}
                </Accordion.Panel>
              </Accordion.Item>
            ))}
          </Accordion>
        </Stack>
      </Card>

      <Card withBorder shadow="sm" radius="md">
        <Stack gap="lg">
          <Box>
            <Title order={4}>Pyodide</Title>
            <Text size="sm" mt={4}>
              The interpreter behind <Anchor href="/python" inherit>Python</Anchor> is{" "}
              <Anchor href="https://github.com/pyodide/pyodide" target="_blank" rel="noopener noreferrer" inherit>
                Pyodide
              </Anchor>
              {pyodide && ` ${pyodide.version}`}, licensed under the{" "}
              <strong>Mozilla Public License 2.0</strong>. The copy served here is the unmodified npm release, kept as
              its own files rather than merged into the rest of the site, and the source it was built from is behind the
              link above. What those files carry is CPython and its standard library, under the{" "}
              <strong>Python Software Foundation License 2.0</strong>, reproduced at the bottom of this page.
            </Text>
          </Box>

          <Box>
            <Title order={4}>PGlite</Title>
            <Text size="sm" mt={4}>
              The Postgres mode of <Anchor href="/sql" inherit>SQL</Anchor> runs on{" "}
              <Anchor href="https://github.com/electric-sql/pglite" target="_blank" rel="noopener noreferrer" inherit>
                PGlite
              </Anchor>
              {pglite && ` ${pglite.version}`}, licensed under the{" "}
              <strong>Apache License 2.0</strong>. The copy served here is the unmodified npm release, its WebAssembly
              kept as files of its own rather than merged into the rest of the site, and the source it was built from is
              behind the link above. What those files carry is PostgreSQL itself, under the{" "}
              <strong>PostgreSQL Licence</strong>, reproduced at the bottom of this page.
            </Text>
          </Box>

          <Box>
            <Title order={4}>SQLite Wasm</Title>
            <Text size="sm" mt={4}>
              The SQLite mode of <Anchor href="/sql" inherit>SQL</Anchor> runs on{" "}
              <Anchor href="https://github.com/sqlite/sqlite-wasm" target="_blank" rel="noopener noreferrer" inherit>
                the SQLite project's own Wasm build
              </Anchor>, wrapped as a module under the{" "}
              <strong>Apache License 2.0</strong>. The engine inside that wrapper is SQLite, which its authors have{" "}
              <Anchor href="https://sqlite.org/copyright.html" target="_blank" rel="noopener noreferrer" inherit>
                dedicated to the public domain
              </Anchor>
              : it is under no licence at all and asks for no notice, so it is named here rather than listed above.
            </Text>
          </Box>

          <Box>
            <Title order={4}>DOMPurify</Title>
            <Text size="sm" mt={4}>
              The preview on <Anchor href="/markdown" inherit>Markdown</Anchor> is sanitised with{" "}
              <Anchor href="https://github.com/cure53/DOMPurify" target="_blank" rel="noopener noreferrer" inherit>
                DOMPurify
              </Anchor>, which its authors offer under either the Mozilla Public License 2.0 or version 2.0 of the
              Apache License. utils+ takes it under{" "}
              <strong>Apache-2.0</strong>, which is the text the package itself ships and the licence utils+ is released
              under.
            </Text>
          </Box>

          <Box>
            <Title order={4}>Roboto</Title>
            <Text size="sm" mt={4}>
              The typeface is{" "}
              <Anchor
                href="https://github.com/googlefonts/roboto-classic"
                target="_blank"
                rel="noopener noreferrer"
                inherit
              >
                Roboto
              </Anchor>, self-hosted here under the{" "}
              <strong>SIL Open Font License 1.1</strong>. It is served unmodified and under its own name, and it is not
              sold, on its own or with anything else. The PDF that <Anchor href="/markdown" inherit>Markdown</Anchor>
              {" "}
              writes is typeset by pdfmake, which carries a copy of the same typeface of its own and embeds it in the
              file it writes: the same faces, arriving inside a package rather than as files beside it, and named here
              for the reason the served copy is.
            </Text>
          </Box>
          <Box>
            <Title order={4}>The faces behind DOCX</Title>
            <Text size="sm" mt={4}>
              <Anchor href="/docx" inherit>DOCX</Anchor>{" "}
              measures and draws a document in faces served from this site rather than the ones it names, Word's own
              coming with Windows and Office under licences that do not let a website serve them. Carlito, Caladea and
              the Liberation fonts stand in for Calibri, Cambria, Times New Roman, Arial and Courier New at the same
              widths, under the{" "}
              <strong>SIL Open Font License 1.1</strong>, and TeX Gyre Adventor stands in for Century Gothic under the
              {" "}
              <strong>GUST Font License</strong>, which is the LaTeX Project Public License with a request attached. All
              of them are served unmodified and under their own names as{" "}
              <Anchor
                href="https://github.com/eigenpal/docx-editor/tree/main/packages/fonts"
                target="_blank"
                rel="noopener noreferrer"
                inherit
              >
                @docx-editor.dev/fonts
              </Anchor>{" "}
              ships them, and that package's entry above reproduces every one of their licences. Everything else is
              drawn in Roboto, served there as the static instances @expo-google-fonts/roboto packages, because what
              measures the text cannot read the variable face the rest of the site is set in. That is{" "}
              <Anchor href="https://harfbuzz.github.io/" target="_blank" rel="noopener noreferrer" inherit>
                HarfBuzz
              </Anchor>, compiled to WebAssembly and shipped inside @docx-editor.dev/core under its own{" "}
              <strong>Old MIT</strong>{" "}
              licence, whose text and copyright holders that package's entry above carries in full.
            </Text>
          </Box>
          <Box>
            <Title order={4}>PDFium</Title>
            <Text size="sm" mt={4}>
              <Anchor href="/pdf" inherit>PDF</Anchor> reads, draws and writes a document with{" "}
              <Anchor href="https://pdfium.googlesource.com/pdfium/" target="_blank" rel="noopener noreferrer" inherit>
                PDFium
              </Anchor>, the engine Chrome reads PDFs with, compiled to WebAssembly by EmbedPDF and served here as the
              unmodified file{" "}
              <Anchor
                href="https://github.com/embedpdf/embed-pdf-viewer/tree/v2/packages/pdfium"
                target="_blank"
                rel="noopener noreferrer"
                inherit
              >
                @embedpdf/pdfium
              </Anchor>
              {pdfium && ` ${pdfium.version}`} ships. PDFium is under the{" "}
              <strong>BSD 3-Clause licence</strong>, with parts under the{" "}
              <strong>Apache License 2.0</strong>, and that package's entry above carries both texts after the wrapper's
              own MIT licence. Compiled into the same file are the libraries PDFium builds with:{" "}
              <strong>FreeType</strong>, for the fonts a document embeds, under the FreeType License;{" "}
              <strong>Little CMS</strong>, for its colour profiles, under the MIT licence;{" "}
              <strong>OpenJPEG</strong>, for JPEG 2000 pictures, under the BSD 2-Clause licence; and{" "}
              <strong>libpng</strong> and{" "}
              <strong>zlib</strong>, under their own licences. Portions of this software are copyright © The FreeType
              Project (
              <Anchor href="https://freetype.org/" target="_blank" rel="noopener noreferrer" inherit>
                www.freetype.org
              </Anchor>
              ). All rights reserved. A typed signature there is drawn in{" "}
              <Anchor
                href="https://github.com/googlefonts/DancingScript"
                target="_blank"
                rel="noopener noreferrer"
                inherit
              >
                Dancing Script
              </Anchor>, self-hosted under the <strong>SIL Open Font License 1.1</strong>{" "}
              and served unmodified and under its own name as @fontsource-variable/dancing-script packages it.
            </Text>
          </Box>
          <Box>
            <Title order={4}>Twemoji Country Flags</Title>
            <Text size="sm" mt={4}>
              The flags on <Anchor href="/countries" inherit>Countries</Anchor> are drawn from the{" "}
              <Anchor
                href="https://github.com/talkjs/country-flag-emoji-polyfill"
                target="_blank"
                rel="noopener noreferrer"
                inherit
              >
                Twemoji Country Flags
              </Anchor>{" "}
              font, self-hosted here because Windows draws a flag emoji as the letter pair it is built from and no stack
              of system fonts can be asked for one instead. The build is TalkJS's, under the{" "}
              <strong>MIT licence</strong>, and is served unmodified; the artwork in it is{" "}
              <Anchor href="https://github.com/jdecked/twemoji" target="_blank" rel="noopener noreferrer" inherit>
                Twemoji
              </Anchor>, used under the{" "}
              <Anchor
                href="https://creativecommons.org/licenses/by/4.0/"
                target="_blank"
                rel="noopener noreferrer"
                inherit
              >
                Creative Commons Attribution 4.0 International licence
              </Anchor>, the change made to it being the packaging of those flags as a font.
            </Text>
          </Box>
          <Box>
            <Title order={4}>world-countries</Title>
            <Text size="sm" mt={4}>
              Everything <Anchor href="/countries" inherit>Countries</Anchor> shows comes from{" "}
              <Anchor href="https://github.com/mledoze/countries" target="_blank" rel="noopener noreferrer" inherit>
                world-countries
              </Anchor>
              {worldCountries && ` ${worldCountries.version}`}, which is the one licence here covering a body of data
              rather than a body of code: the{" "}
              <strong>Open Database License 1.0</strong>, whose full text is above. The copy served is the unmodified
              npm release, read by that page and by nothing else, and utils+ has produced no derived database from it.
            </Text>
          </Box>
          <Box>
            <Title order={4}>The Internet number registries</Title>
            <Text size="sm" mt={4}>
              <Anchor href="/ip-address" inherit>IP Address</Anchor>{" "}
              answers from three publications no package above declares, because none of them arrives through npm. The
              block and AS number registries are{" "}
              <Anchor href="https://www.iana.org/" target="_blank" rel="noopener noreferrer" inherit>IANA</Anchor>'s,
              dedicated with the IETF under <strong>CC0 1.0</strong>{" "}
              and intended, in their own words, to be embedded in software implementations. What each of the five
              regional registries has delegated, and to which country, comes from the daily statistics the{" "}
              <Anchor href="https://www.nro.net/" target="_blank" rel="noopener noreferrer" inherit>
                Number Resource Organization
              </Anchor>{" "}
              combines — freely downloadable, and a separate publication from the whois database, which is why the page
              names registries and never the holder of an address. And the route origins are the{" "}
              <strong>Route Origin Authorisations</strong>{" "}
              the five registries publish under RPKI: statements signed by the holder of a prefix that a given AS may
              originate it, validated daily by the RIPE NCC and signed so that anybody may check them. All three are
              read at build time and served from this origin, so the page makes no request of any registry.
            </Text>
          </Box>
          <Box>
            <Title order={4}>Natural Earth</Title>
            <Text size="sm" mt={4}>
              The map on <Anchor href="/countries" inherit>Countries</Anchor> is drawn from{" "}
              <Anchor href="https://www.naturalearthdata.com/" target="_blank" rel="noopener noreferrer" inherit>
                Natural Earth
              </Anchor>, whose authors have placed every version of its map data in the{" "}
              <strong>public domain</strong>: no permission is asked for and no notice is required, and this one is here
              because a boundary is somebody's work whether or not they ask to be named for it. The boundaries are taken
              at build time from a pinned release of{" "}
              <Anchor
                href="https://github.com/nvkelso/natural-earth-vector"
                target="_blank"
                rel="noopener noreferrer"
                inherit
              >
                natural-earth-vector
              </Anchor>{" "}
              and simplified for drawing, so what is served here is derived from that data rather than a copy of it —
              which is also why no package above names it. The points of view are Natural Earth's own: it publishes a
              default set of boundaries drawn from who holds the ground, and a separate set for each of thirty-one
              countries drawn as that country's own law and conventions have them. Which one a reader is shown follows
              the country their browser reports, and the map says underneath it which one that was.
            </Text>
          </Box>
          <Box>
            <Title order={4}>The Unicode Character Database</Title>
            <Text size="sm" mt={4}>
              Everything <Anchor href="/unicode" inherit>Unicode</Anchor> says about a character is read out of the{" "}
              <Anchor
                href="https://www.unicode.org/Public/UCD/latest/"
                target="_blank"
                rel="noopener noreferrer"
                inherit
              >
                Unicode Character Database
              </Anchor>{" "}
              , the confusables table published beside it for{" "}
              <Anchor href="https://www.unicode.org/reports/tr39/" target="_blank" rel="noopener noreferrer" inherit>
                UTS #39
              </Anchor>{" "}
              and the emoji list published for{" "}
              <Anchor href="https://www.unicode.org/reports/tr51/" target="_blank" rel="noopener noreferrer" inherit>
                UTS #51
              </Anchor>, all under the{" "}
              <strong>Unicode License v3</strong>, whose text is at the bottom of this page. The names, categories,
              scripts, blocks, versions and emoji are taken at build time from one pinned version and served here as
              files of their own, rewritten into the shape that page reads them in rather than copied across, which is
              why no package above names them.
            </Text>
          </Box>
          <Box>
            <Title order={4}>libphonenumber</Title>
            <Text size="sm" mt={4}>
              Everything <Anchor href="/phone-number" inherit>Phone Number</Anchor> reads comes out of Google's{" "}
              <Anchor href="https://github.com/google/libphonenumber" target="_blank" rel="noopener noreferrer" inherit>
                libphonenumber
              </Anchor>, under the{" "}
              <strong>Apache License 2.0</strong>, whose full text is above. The numbering plans are the copy compiled
              into{" "}
              <Anchor
                href="https://gitlab.com/catamphetamine/libphonenumber-js"
                target="_blank"
                rel="noopener noreferrer"
                inherit
              >
                libphonenumber-js
              </Anchor>
              {libphonenumberJs && ` ${libphonenumberJs.version}`}, which is listed above under its own{" "}
              <strong>MIT licence</strong>, with the Apache text it ships for the plans inside it beside that. The
              geocoding, carrier and timezone maps are taken unmodified from that repository's published releases at
              build time and served here as files of their own, which is why no package above names them.
            </Text>
          </Box>
          <Box>
            <Title order={4}>OpenPGP.js</Title>
            <Text size="sm" mt={4}>
              The PGP keys on <Anchor href="/keygen" inherit>Keygen</Anchor> are generated by{" "}
              <Anchor href="https://github.com/openpgpjs/openpgpjs" target="_blank" rel="noopener noreferrer" inherit>
                OpenPGP.js
              </Anchor>
              {openpgp && ` ${openpgp.version}`}, which is licensed under the{" "}
              <strong>GNU Lesser General Public License, version 3 or later</strong>. utils+ uses it as a library and
              has not modified it: the copy served here is the unmodified npm release, kept in its own file rather than
              merged into the rest of the site, so it can be swapped for another build of the same library. The full
              source of utils+ is at{" "}
              <Anchor href={REPO} target="_blank" rel="noopener noreferrer" inherit>{REPO_LABEL}</Anchor>, which is
              everything needed to rebuild this site against a modified OpenPGP.js. Both licence texts the LGPL calls
              for are at the bottom of this page.
            </Text>
          </Box>
        </Stack>
      </Card>

      <Card withBorder shadow="sm" radius="md">
        <Stack gap="sm">
          <Title order={4}>The PostgreSQL Licence</Title>
          <Text size="sm" c="dimmed">
            Reproduced because the PostgreSQL server PGlite carries is served from this site, and it arrives inside a
            package rather than as one. It does not govern utils+ itself.
          </Text>
          <Licence url={postgresUrl} />
        </Stack>
      </Card>
      <Card withBorder shadow="sm" radius="md">
        <Stack gap="sm">
          <Title order={4}>Python Software Foundation License, version 2</Title>
          <Text size="sm" c="dimmed">
            Reproduced because the Python standard library Pyodide carries is served from this site, and it is the one
            licence here that arrives inside a package rather than as one. It does not govern utils+ itself.
          </Text>
          <Licence url={pythonUrl} />
        </Stack>
      </Card>
      <Card withBorder shadow="sm" radius="md">
        <Stack gap="sm">
          <Title order={4}>Unicode License v3</Title>
          <Text size="sm" c="dimmed">
            Reproduced because the character database it covers is served from this site, and it asks for this notice to
            appear with any copy of the data. It does not govern utils+ itself.
          </Text>
          <Licence url={unicodeUrl} />
        </Stack>
      </Card>
      <Card withBorder shadow="sm" radius="md">
        <Stack gap="sm">
          <Title order={4}>GNU General Public License, version 3</Title>
          <Text size="sm" c="dimmed">
            Reproduced because the GNU LGPL v3, which covers OpenPGP.js above, is written as a set of additional
            permissions on top of this licence and calls for both texts to be conveyed together. It does not govern
            utils+ itself.
          </Text>
          <Licence url={gplUrl} />
        </Stack>
      </Card>
    </Stack>
  );
}

// The text is a file of its own under assets/license/ and is asked for when somebody opens the row it belongs to:
// a hundred and sixty licences is most of a megabyte, and a reader who came to read one of them wants one of them.
function Licence({ url }: { url: string | undefined }) {
  // Undefined while it is being read, null when it could not be, which is the one thing a licence must not do quietly.
  const [text, setText] = useState<string | null | undefined>(() => LOADED.get(url ?? ""));

  useEffect(() => {
    if (text !== undefined) return;
    let reading = true;
    read(url).then((loaded) => reading && setText(loaded));
    return () => {
      reading = false;
    };
  }, [url, text]);

  if (text === undefined) {
    return (
      <Group gap="xs">
        <Loader size="xs" />
        <Text size="sm" c="dimmed">Reading the licence…</Text>
      </Group>
    );
  }

  if (text === null) {
    return <Text size="sm" c="dimmed">This licence could not be read; the source is linked above.</Text>;
  }
  return <LicenceText>{text}</LicenceText>;
}

function LicenceText({ children }: { children: string }) {
  return (
    <Text
      component="pre"
      size="xs"
      ff="monospace"
      // A licence is a document to be reproduced verbatim, so it wraps and scrolls rather than being reflowed.
      style={{ whiteSpace: "pre-wrap", maxHeight: "22rem", overflow: "auto", margin: 0 }}
    >
      {children}
    </Text>
  );
}

function read(url: string | undefined): Promise<string | null> {
  if (!url) return Promise.resolve(null);

  const reading = READING.get(url) ?? fetch(url)
    .then((response) => response.ok ? response.text() : null)
    .catch(() => null)
    .then((body) => {
      const text = body === null ? null : body.trim();
      // A failure is forgotten so that opening the row again asks again; what arrived is kept for the rest of the visit.
      if (text === null) READING.delete(url);
      else LOADED.set(url, text);
      return text;
    });

  READING.set(url, reading);
  return reading;
}

// Both are keyed by the address, so a licence two packages happen to share is fetched and held once.
const READING = new Map<string, Promise<string | null>>();
const LOADED = new Map<string, string>();

interface Attribution {
  name: string;
  version: string;
  license: string;
  publisher: string;
  url: string;
  file: string;
  reconstructed?: boolean;
}

// The generator writes optional members only where they apply, which infers as a union rather than one shape.
// Shuffled once as the chunk loads, not per render: the order holds still while the filter is typed into.
// A copy, because the array behind it is the imported module's own.
const packages = [...(attributions.packages as Attribution[])];
shuffle(packages);

// The address of every licence and the text of none of them: the glob is `?url`, so each file is emitted under
// assets/license/ and hashed by its own contents, and what this chunk carries is a hundred and sixty addresses.
const LICENCES: Record<string, string> = Object.fromEntries(
  Object.entries(
    { "../attribution/license/acorn.txt": __glob_0, "../attribution/license/age-encryption.txt": __glob_1, "../attribution/license/any-promise.txt": __glob_2, "../attribution/license/anynum.txt": __glob_3, "../attribution/license/aria-hidden.txt": __glob_4, "../attribution/license/babel-runtime.txt": __glob_5, "../attribution/license/base64-js.txt": __glob_6, "../attribution/license/bcrypt-pbkdf.txt": __glob_7, "../attribution/license/bcryptjs.txt": __glob_8, "../attribution/license/bidi-js.txt": __glob_9, "../attribution/license/brotli.txt": __glob_10, "../attribution/license/browserify-zlib.txt": __glob_11, "../attribution/license/class-variance-authority.txt": __glob_12, "../attribution/license/clone.txt": __glob_13, "../attribution/license/clsx.txt": __glob_14, "../attribution/license/codemirror-autocomplete.txt": __glob_15, "../attribution/license/codemirror-commands.txt": __glob_16, "../attribution/license/codemirror-lang-cpp.txt": __glob_17, "../attribution/license/codemirror-lang-css.txt": __glob_18, "../attribution/license/codemirror-lang-go.txt": __glob_19, "../attribution/license/codemirror-lang-html.txt": __glob_20, "../attribution/license/codemirror-lang-java.txt": __glob_21, "../attribution/license/codemirror-lang-javascript.txt": __glob_22, "../attribution/license/codemirror-lang-json.txt": __glob_23, "../attribution/license/codemirror-lang-markdown.txt": __glob_24, "../attribution/license/codemirror-lang-php.txt": __glob_25, "../attribution/license/codemirror-lang-python.txt": __glob_26, "../attribution/license/codemirror-lang-rust.txt": __glob_27, "../attribution/license/codemirror-lang-sql.txt": __glob_28, "../attribution/license/codemirror-lang-xml.txt": __glob_29, "../attribution/license/codemirror-lang-yaml.txt": __glob_30, "../attribution/license/codemirror-language.txt": __glob_31, "../attribution/license/codemirror-legacy-modes.txt": __glob_32, "../attribution/license/codemirror-lint.txt": __glob_33, "../attribution/license/codemirror-search.txt": __glob_34, "../attribution/license/codemirror-state.txt": __glob_35, "../attribution/license/codemirror-theme-one-dark.txt": __glob_36, "../attribution/license/codemirror-view.txt": __glob_37, "../attribution/license/codemirror.txt": __glob_38, "../attribution/license/commander.txt": __glob_39, "../attribution/license/country-flag-emoji-polyfill.txt": __glob_40, "../attribution/license/crelt.txt": __glob_41, "../attribution/license/csstype.txt": __glob_42, "../attribution/license/dayjs.txt": __glob_43, "../attribution/license/detect-node-es.txt": __glob_44, "../attribution/license/dfa.txt": __glob_45, "../attribution/license/dnd-kit-accessibility.txt": __glob_46, "../attribution/license/dnd-kit-core.txt": __glob_47, "../attribution/license/dnd-kit-modifiers.txt": __glob_48, "../attribution/license/dnd-kit-sortable.txt": __glob_49, "../attribution/license/dnd-kit-utilities.txt": __glob_50, "../attribution/license/docx-editor.dev-core.txt": __glob_51, "../attribution/license/docx-editor.dev-fonts.txt": __glob_52, "../attribution/license/docx-editor.dev-i18n.txt": __glob_53, "../attribution/license/docx-editor.dev-react.txt": __glob_54, "../attribution/license/dompurify.txt": __glob_55, "../attribution/license/electric-sql-pglite.txt": __glob_56, "../attribution/license/embedpdf-core.txt": __glob_57, "../attribution/license/embedpdf-engines.txt": __glob_58, "../attribution/license/embedpdf-fonts-arabic.txt": __glob_59, "../attribution/license/embedpdf-fonts-hebrew.txt": __glob_60, "../attribution/license/embedpdf-fonts-jp.txt": __glob_61, "../attribution/license/embedpdf-fonts-kr.txt": __glob_62, "../attribution/license/embedpdf-fonts-latin.txt": __glob_63, "../attribution/license/embedpdf-fonts-sc.txt": __glob_64, "../attribution/license/embedpdf-fonts-tc.txt": __glob_65, "../attribution/license/embedpdf-models.txt": __glob_66, "../attribution/license/embedpdf-pdfium.txt": __glob_67, "../attribution/license/embedpdf-plugin-annotation.txt": __glob_68, "../attribution/license/embedpdf-plugin-document-manager.txt": __glob_69, "../attribution/license/embedpdf-plugin-export.txt": __glob_70, "../attribution/license/embedpdf-plugin-history.txt": __glob_71, "../attribution/license/embedpdf-plugin-interaction-manager.txt": __glob_72, "../attribution/license/embedpdf-plugin-render.txt": __glob_73, "../attribution/license/embedpdf-plugin-scroll.txt": __glob_74, "../attribution/license/embedpdf-plugin-selection.txt": __glob_75, "../attribution/license/embedpdf-plugin-signature.txt": __glob_76, "../attribution/license/embedpdf-plugin-viewport.txt": __glob_77, "../attribution/license/embedpdf-plugin-zoom.txt": __glob_78, "../attribution/license/embedpdf-utils.txt": __glob_79, "../attribution/license/emf-converter.txt": __glob_80, "../attribution/license/eslint-community-regexpp.txt": __glob_81, "../attribution/license/expo-google-fonts-roboto.txt": __glob_82, "../attribution/license/fast-deep-equal.txt": __glob_83, "../attribution/license/fast-xml-builder.txt": __glob_84, "../attribution/license/fast-xml-parser.txt": __glob_85, "../attribution/license/fdir.txt": __glob_86, "../attribution/license/fflate.txt": __glob_87, "../attribution/license/floating-ui-core.txt": __glob_88, "../attribution/license/floating-ui-dom.txt": __glob_89, "../attribution/license/floating-ui-react-dom.txt": __glob_90, "../attribution/license/floating-ui-react.txt": __glob_91, "../attribution/license/floating-ui-utils.txt": __glob_92, "../attribution/license/fontkit.txt": __glob_93, "../attribution/license/fontsource-variable-dancing-script.txt": __glob_94, "../attribution/license/fontsource-variable-roboto.txt": __glob_95, "../attribution/license/fuse.js.txt": __glob_96, "../attribution/license/get-nonce.txt": __glob_97, "../attribution/license/harfbuzzjs.txt": __glob_98, "../attribution/license/is-unsafe.txt": __glob_99, "../attribution/license/jitl-quickjs-ffi-types.txt": __glob_100, "../attribution/license/jitl-quickjs-ng-wasmfile-release-sync.txt": __glob_101, "../attribution/license/jose.txt": __glob_102, "../attribution/license/jridgewell-gen-mapping.txt": __glob_103, "../attribution/license/jridgewell-resolve-uri.txt": __glob_104, "../attribution/license/jridgewell-sourcemap-codec.txt": __glob_105, "../attribution/license/jridgewell-trace-mapping.txt": __glob_106, "../attribution/license/js-md5.txt": __glob_107, "../attribution/license/lezer-common.txt": __glob_108, "../attribution/license/lezer-cpp.txt": __glob_109, "../attribution/license/lezer-css.txt": __glob_110, "../attribution/license/lezer-go.txt": __glob_111, "../attribution/license/lezer-highlight.txt": __glob_112, "../attribution/license/lezer-html.txt": __glob_113, "../attribution/license/lezer-java.txt": __glob_114, "../attribution/license/lezer-javascript.txt": __glob_115, "../attribution/license/lezer-json.txt": __glob_116, "../attribution/license/lezer-lr.txt": __glob_117, "../attribution/license/lezer-markdown.txt": __glob_118, "../attribution/license/lezer-php.txt": __glob_119, "../attribution/license/lezer-python.txt": __glob_120, "../attribution/license/lezer-rust.txt": __glob_121, "../attribution/license/lezer-xml.txt": __glob_122, "../attribution/license/lezer-yaml.txt": __glob_123, "../attribution/license/libphonenumber-js.txt": __glob_124, "../attribution/license/linebreak.txt": __glob_125, "../attribution/license/lines-and-columns.txt": __glob_126, "../attribution/license/mantine-core.txt": __glob_127, "../attribution/license/mantine-datatable.txt": __glob_128, "../attribution/license/mantine-dates.txt": __glob_129, "../attribution/license/mantine-hooks.txt": __glob_130, "../attribution/license/mantine-spotlight.txt": __glob_131, "../attribution/license/mantine-store.txt": __glob_132, "../attribution/license/marijn-find-cluster-break.txt": __glob_133, "../attribution/license/marked.txt": __glob_134, "../attribution/license/mz.txt": __glob_135, "../attribution/license/noble-ciphers.txt": __glob_136, "../attribution/license/noble-curves.txt": __glob_137, "../attribution/license/noble-hashes.txt": __glob_138, "../attribution/license/noble-post-quantum.txt": __glob_139, "../attribution/license/nodable-entities.txt": __glob_140, "../attribution/license/object-assign.txt": __glob_141, "../attribution/license/openpgp.txt": __glob_142, "../attribution/license/orderedmap.txt": __glob_143, "../attribution/license/pako.txt": __glob_144, "../attribution/license/path-expression-matcher.txt": __glob_145, "../attribution/license/pdfkit.txt": __glob_146, "../attribution/license/pdfmake.txt": __glob_147, "../attribution/license/picomatch.txt": __glob_148, "../attribution/license/pirates.txt": __glob_149, "../attribution/license/png-js.txt": __glob_150, "../attribution/license/prosemirror-commands.txt": __glob_151, "../attribution/license/prosemirror-keymap.txt": __glob_152, "../attribution/license/prosemirror-model.txt": __glob_153, "../attribution/license/prosemirror-state.txt": __glob_154, "../attribution/license/prosemirror-transform.txt": __glob_155, "../attribution/license/prosemirror-view.txt": __glob_156, "../attribution/license/pyodide.txt": __glob_157, "../attribution/license/qrcode-generator.txt": __glob_158, "../attribution/license/quickjs-emscripten-core.txt": __glob_159, "../attribution/license/radix-ui-number.txt": __glob_160, "../attribution/license/radix-ui-primitive.txt": __glob_161, "../attribution/license/radix-ui-react-arrow.txt": __glob_162, "../attribution/license/radix-ui-react-collection.txt": __glob_163, "../attribution/license/radix-ui-react-compose-refs.txt": __glob_164, "../attribution/license/radix-ui-react-context.txt": __glob_165, "../attribution/license/radix-ui-react-direction.txt": __glob_166, "../attribution/license/radix-ui-react-dismissable-layer.txt": __glob_167, "../attribution/license/radix-ui-react-focus-guards.txt": __glob_168, "../attribution/license/radix-ui-react-focus-scope.txt": __glob_169, "../attribution/license/radix-ui-react-id.txt": __glob_170, "../attribution/license/radix-ui-react-popper.txt": __glob_171, "../attribution/license/radix-ui-react-portal.txt": __glob_172, "../attribution/license/radix-ui-react-presence.txt": __glob_173, "../attribution/license/radix-ui-react-primitive.txt": __glob_174, "../attribution/license/radix-ui-react-select.txt": __glob_175, "../attribution/license/radix-ui-react-slot.txt": __glob_176, "../attribution/license/radix-ui-react-use-callback-ref.txt": __glob_177, "../attribution/license/radix-ui-react-use-controllable-state.txt": __glob_178, "../attribution/license/radix-ui-react-use-effect-event.txt": __glob_179, "../attribution/license/radix-ui-react-use-layout-effect.txt": __glob_180, "../attribution/license/radix-ui-react-use-previous.txt": __glob_181, "../attribution/license/radix-ui-react-use-rect.txt": __glob_182, "../attribution/license/radix-ui-react-use-size.txt": __glob_183, "../attribution/license/radix-ui-react-visually-hidden.txt": __glob_184, "../attribution/license/radix-ui-rect.txt": __glob_185, "../attribution/license/react-dom.txt": __glob_186, "../attribution/license/react-number-format.txt": __glob_187, "../attribution/license/react-remove-scroll-bar.txt": __glob_188, "../attribution/license/react-remove-scroll.txt": __glob_189, "../attribution/license/react-style-singleton.txt": __glob_190, "../attribution/license/react.txt": __glob_191, "../attribution/license/require-from-string.txt": __glob_192, "../attribution/license/restructure.txt": __glob_193, "../attribution/license/sax.txt": __glob_194, "../attribution/license/scheduler.txt": __glob_195, "../attribution/license/scure-base.txt": __glob_196, "../attribution/license/smol-toml.txt": __glob_197, "../attribution/license/sqlite.org-sqlite-wasm.txt": __glob_198, "../attribution/license/strnum.txt": __glob_199, "../attribution/license/style-mod.txt": __glob_200, "../attribution/license/sucrase.txt": __glob_201, "../attribution/license/swc-helpers.txt": __glob_202, "../attribution/license/tabbable.txt": __glob_203, "../attribution/license/tabler-icons-react.txt": __glob_204, "../attribution/license/tabler-icons.txt": __glob_205, "../attribution/license/tagged-tag.txt": __glob_206, "../attribution/license/thenify-all.txt": __glob_207, "../attribution/license/thenify.txt": __glob_208, "../attribution/license/tiny-inflate.txt": __glob_209, "../attribution/license/tinyglobby.txt": __glob_210, "../attribution/license/ts-interface-checker.txt": __glob_211, "../attribution/license/tslib.txt": __glob_212, "../attribution/license/tweetnacl.txt": __glob_213, "../attribution/license/type-fest.txt": __glob_214, "../attribution/license/types-emscripten.txt": __glob_215, "../attribution/license/types-react-dom.txt": __glob_216, "../attribution/license/types-react.txt": __glob_217, "../attribution/license/types-trusted-types.txt": __glob_218, "../attribution/license/uiw-codemirror-extensions-basic-setup.txt": __glob_219, "../attribution/license/uiw-react-codemirror.txt": __glob_220, "../attribution/license/unicode-properties.txt": __glob_221, "../attribution/license/unicode-trie.txt": __glob_222, "../attribution/license/use-callback-ref.txt": __glob_223, "../attribution/license/use-sidecar.txt": __glob_224, "../attribution/license/utif2.txt": __glob_225, "../attribution/license/vvo-tzdb.txt": __glob_226, "../attribution/license/w3c-keyname.txt": __glob_227, "../attribution/license/world-countries.txt": __glob_228, "../attribution/license/ws.txt": __glob_229, "../attribution/license/xml-naming.txt": __glob_230, "../attribution/license/xmldoc.txt": __glob_231, "../attribution/license/yaml.txt": __glob_232 } as Record<
      string,
      string
    >,
  ).map(([path, url]) => [path.slice(path.lastIndexOf("/") + 1, -".txt".length), url]),
);
const openpgp = packages.find((pkg) => pkg.name === "openpgp");
const pyodide = packages.find((pkg) => pkg.name === "pyodide");
const pglite = packages.find((pkg) => pkg.name === "@electric-sql/pglite");
const worldCountries = packages.find((pkg) => pkg.name === "world-countries");
const libphonenumberJs = packages.find((pkg) => pkg.name === "libphonenumber-js");
const pdfium = packages.find((pkg) => pkg.name === "@embedpdf/pdfium");

const summary = Object.entries(
  packages.reduce<Record<string, number>>((counts, pkg) => {
    counts[pkg.license] = (counts[pkg.license] ?? 0) + 1;
    return counts;
  }, {}),
).sort((a, b) => b[1] - a[1] || a[0].localeCompare(b[0]));

const REPO_LABEL = "manualpilot/utils.plus";
const REPO = `https://github.com/${REPO_LABEL}`;

import __glob_0 from "../attribution/license/acorn.txt?url";
import __glob_1 from "../attribution/license/age-encryption.txt?url";
import __glob_2 from "../attribution/license/any-promise.txt?url";
import __glob_3 from "../attribution/license/anynum.txt?url";
import __glob_4 from "../attribution/license/aria-hidden.txt?url";
import __glob_5 from "../attribution/license/babel-runtime.txt?url";
import __glob_6 from "../attribution/license/base64-js.txt?url";
import __glob_7 from "../attribution/license/bcrypt-pbkdf.txt?url";
import __glob_8 from "../attribution/license/bcryptjs.txt?url";
import __glob_9 from "../attribution/license/bidi-js.txt?url";
import __glob_10 from "../attribution/license/brotli.txt?url";
import __glob_11 from "../attribution/license/browserify-zlib.txt?url";
import __glob_12 from "../attribution/license/class-variance-authority.txt?url";
import __glob_13 from "../attribution/license/clone.txt?url";
import __glob_14 from "../attribution/license/clsx.txt?url";
import __glob_15 from "../attribution/license/codemirror-autocomplete.txt?url";
import __glob_16 from "../attribution/license/codemirror-commands.txt?url";
import __glob_17 from "../attribution/license/codemirror-lang-cpp.txt?url";
import __glob_18 from "../attribution/license/codemirror-lang-css.txt?url";
import __glob_19 from "../attribution/license/codemirror-lang-go.txt?url";
import __glob_20 from "../attribution/license/codemirror-lang-html.txt?url";
import __glob_21 from "../attribution/license/codemirror-lang-java.txt?url";
import __glob_22 from "../attribution/license/codemirror-lang-javascript.txt?url";
import __glob_23 from "../attribution/license/codemirror-lang-json.txt?url";
import __glob_24 from "../attribution/license/codemirror-lang-markdown.txt?url";
import __glob_25 from "../attribution/license/codemirror-lang-php.txt?url";
import __glob_26 from "../attribution/license/codemirror-lang-python.txt?url";
import __glob_27 from "../attribution/license/codemirror-lang-rust.txt?url";
import __glob_28 from "../attribution/license/codemirror-lang-sql.txt?url";
import __glob_29 from "../attribution/license/codemirror-lang-xml.txt?url";
import __glob_30 from "../attribution/license/codemirror-lang-yaml.txt?url";
import __glob_31 from "../attribution/license/codemirror-language.txt?url";
import __glob_32 from "../attribution/license/codemirror-legacy-modes.txt?url";
import __glob_33 from "../attribution/license/codemirror-lint.txt?url";
import __glob_34 from "../attribution/license/codemirror-search.txt?url";
import __glob_35 from "../attribution/license/codemirror-state.txt?url";
import __glob_36 from "../attribution/license/codemirror-theme-one-dark.txt?url";
import __glob_37 from "../attribution/license/codemirror-view.txt?url";
import __glob_38 from "../attribution/license/codemirror.txt?url";
import __glob_39 from "../attribution/license/commander.txt?url";
import __glob_40 from "../attribution/license/country-flag-emoji-polyfill.txt?url";
import __glob_41 from "../attribution/license/crelt.txt?url";
import __glob_42 from "../attribution/license/csstype.txt?url";
import __glob_43 from "../attribution/license/dayjs.txt?url";
import __glob_44 from "../attribution/license/detect-node-es.txt?url";
import __glob_45 from "../attribution/license/dfa.txt?url";
import __glob_46 from "../attribution/license/dnd-kit-accessibility.txt?url";
import __glob_47 from "../attribution/license/dnd-kit-core.txt?url";
import __glob_48 from "../attribution/license/dnd-kit-modifiers.txt?url";
import __glob_49 from "../attribution/license/dnd-kit-sortable.txt?url";
import __glob_50 from "../attribution/license/dnd-kit-utilities.txt?url";
import __glob_51 from "../attribution/license/docx-editor.dev-core.txt?url";
import __glob_52 from "../attribution/license/docx-editor.dev-fonts.txt?url";
import __glob_53 from "../attribution/license/docx-editor.dev-i18n.txt?url";
import __glob_54 from "../attribution/license/docx-editor.dev-react.txt?url";
import __glob_55 from "../attribution/license/dompurify.txt?url";
import __glob_56 from "../attribution/license/electric-sql-pglite.txt?url";
import __glob_57 from "../attribution/license/embedpdf-core.txt?url";
import __glob_58 from "../attribution/license/embedpdf-engines.txt?url";
import __glob_59 from "../attribution/license/embedpdf-fonts-arabic.txt?url";
import __glob_60 from "../attribution/license/embedpdf-fonts-hebrew.txt?url";
import __glob_61 from "../attribution/license/embedpdf-fonts-jp.txt?url";
import __glob_62 from "../attribution/license/embedpdf-fonts-kr.txt?url";
import __glob_63 from "../attribution/license/embedpdf-fonts-latin.txt?url";
import __glob_64 from "../attribution/license/embedpdf-fonts-sc.txt?url";
import __glob_65 from "../attribution/license/embedpdf-fonts-tc.txt?url";
import __glob_66 from "../attribution/license/embedpdf-models.txt?url";
import __glob_67 from "../attribution/license/embedpdf-pdfium.txt?url";
import __glob_68 from "../attribution/license/embedpdf-plugin-annotation.txt?url";
import __glob_69 from "../attribution/license/embedpdf-plugin-document-manager.txt?url";
import __glob_70 from "../attribution/license/embedpdf-plugin-export.txt?url";
import __glob_71 from "../attribution/license/embedpdf-plugin-history.txt?url";
import __glob_72 from "../attribution/license/embedpdf-plugin-interaction-manager.txt?url";
import __glob_73 from "../attribution/license/embedpdf-plugin-render.txt?url";
import __glob_74 from "../attribution/license/embedpdf-plugin-scroll.txt?url";
import __glob_75 from "../attribution/license/embedpdf-plugin-selection.txt?url";
import __glob_76 from "../attribution/license/embedpdf-plugin-signature.txt?url";
import __glob_77 from "../attribution/license/embedpdf-plugin-viewport.txt?url";
import __glob_78 from "../attribution/license/embedpdf-plugin-zoom.txt?url";
import __glob_79 from "../attribution/license/embedpdf-utils.txt?url";
import __glob_80 from "../attribution/license/emf-converter.txt?url";
import __glob_81 from "../attribution/license/eslint-community-regexpp.txt?url";
import __glob_82 from "../attribution/license/expo-google-fonts-roboto.txt?url";
import __glob_83 from "../attribution/license/fast-deep-equal.txt?url";
import __glob_84 from "../attribution/license/fast-xml-builder.txt?url";
import __glob_85 from "../attribution/license/fast-xml-parser.txt?url";
import __glob_86 from "../attribution/license/fdir.txt?url";
import __glob_87 from "../attribution/license/fflate.txt?url";
import __glob_88 from "../attribution/license/floating-ui-core.txt?url";
import __glob_89 from "../attribution/license/floating-ui-dom.txt?url";
import __glob_90 from "../attribution/license/floating-ui-react-dom.txt?url";
import __glob_91 from "../attribution/license/floating-ui-react.txt?url";
import __glob_92 from "../attribution/license/floating-ui-utils.txt?url";
import __glob_93 from "../attribution/license/fontkit.txt?url";
import __glob_94 from "../attribution/license/fontsource-variable-dancing-script.txt?url";
import __glob_95 from "../attribution/license/fontsource-variable-roboto.txt?url";
import __glob_96 from "../attribution/license/fuse.js.txt?url";
import __glob_97 from "../attribution/license/get-nonce.txt?url";
import __glob_98 from "../attribution/license/harfbuzzjs.txt?url";
import __glob_99 from "../attribution/license/is-unsafe.txt?url";
import __glob_100 from "../attribution/license/jitl-quickjs-ffi-types.txt?url";
import __glob_101 from "../attribution/license/jitl-quickjs-ng-wasmfile-release-sync.txt?url";
import __glob_102 from "../attribution/license/jose.txt?url";
import __glob_103 from "../attribution/license/jridgewell-gen-mapping.txt?url";
import __glob_104 from "../attribution/license/jridgewell-resolve-uri.txt?url";
import __glob_105 from "../attribution/license/jridgewell-sourcemap-codec.txt?url";
import __glob_106 from "../attribution/license/jridgewell-trace-mapping.txt?url";
import __glob_107 from "../attribution/license/js-md5.txt?url";
import __glob_108 from "../attribution/license/lezer-common.txt?url";
import __glob_109 from "../attribution/license/lezer-cpp.txt?url";
import __glob_110 from "../attribution/license/lezer-css.txt?url";
import __glob_111 from "../attribution/license/lezer-go.txt?url";
import __glob_112 from "../attribution/license/lezer-highlight.txt?url";
import __glob_113 from "../attribution/license/lezer-html.txt?url";
import __glob_114 from "../attribution/license/lezer-java.txt?url";
import __glob_115 from "../attribution/license/lezer-javascript.txt?url";
import __glob_116 from "../attribution/license/lezer-json.txt?url";
import __glob_117 from "../attribution/license/lezer-lr.txt?url";
import __glob_118 from "../attribution/license/lezer-markdown.txt?url";
import __glob_119 from "../attribution/license/lezer-php.txt?url";
import __glob_120 from "../attribution/license/lezer-python.txt?url";
import __glob_121 from "../attribution/license/lezer-rust.txt?url";
import __glob_122 from "../attribution/license/lezer-xml.txt?url";
import __glob_123 from "../attribution/license/lezer-yaml.txt?url";
import __glob_124 from "../attribution/license/libphonenumber-js.txt?url";
import __glob_125 from "../attribution/license/linebreak.txt?url";
import __glob_126 from "../attribution/license/lines-and-columns.txt?url";
import __glob_127 from "../attribution/license/mantine-core.txt?url";
import __glob_128 from "../attribution/license/mantine-datatable.txt?url";
import __glob_129 from "../attribution/license/mantine-dates.txt?url";
import __glob_130 from "../attribution/license/mantine-hooks.txt?url";
import __glob_131 from "../attribution/license/mantine-spotlight.txt?url";
import __glob_132 from "../attribution/license/mantine-store.txt?url";
import __glob_133 from "../attribution/license/marijn-find-cluster-break.txt?url";
import __glob_134 from "../attribution/license/marked.txt?url";
import __glob_135 from "../attribution/license/mz.txt?url";
import __glob_136 from "../attribution/license/noble-ciphers.txt?url";
import __glob_137 from "../attribution/license/noble-curves.txt?url";
import __glob_138 from "../attribution/license/noble-hashes.txt?url";
import __glob_139 from "../attribution/license/noble-post-quantum.txt?url";
import __glob_140 from "../attribution/license/nodable-entities.txt?url";
import __glob_141 from "../attribution/license/object-assign.txt?url";
import __glob_142 from "../attribution/license/openpgp.txt?url";
import __glob_143 from "../attribution/license/orderedmap.txt?url";
import __glob_144 from "../attribution/license/pako.txt?url";
import __glob_145 from "../attribution/license/path-expression-matcher.txt?url";
import __glob_146 from "../attribution/license/pdfkit.txt?url";
import __glob_147 from "../attribution/license/pdfmake.txt?url";
import __glob_148 from "../attribution/license/picomatch.txt?url";
import __glob_149 from "../attribution/license/pirates.txt?url";
import __glob_150 from "../attribution/license/png-js.txt?url";
import __glob_151 from "../attribution/license/prosemirror-commands.txt?url";
import __glob_152 from "../attribution/license/prosemirror-keymap.txt?url";
import __glob_153 from "../attribution/license/prosemirror-model.txt?url";
import __glob_154 from "../attribution/license/prosemirror-state.txt?url";
import __glob_155 from "../attribution/license/prosemirror-transform.txt?url";
import __glob_156 from "../attribution/license/prosemirror-view.txt?url";
import __glob_157 from "../attribution/license/pyodide.txt?url";
import __glob_158 from "../attribution/license/qrcode-generator.txt?url";
import __glob_159 from "../attribution/license/quickjs-emscripten-core.txt?url";
import __glob_160 from "../attribution/license/radix-ui-number.txt?url";
import __glob_161 from "../attribution/license/radix-ui-primitive.txt?url";
import __glob_162 from "../attribution/license/radix-ui-react-arrow.txt?url";
import __glob_163 from "../attribution/license/radix-ui-react-collection.txt?url";
import __glob_164 from "../attribution/license/radix-ui-react-compose-refs.txt?url";
import __glob_165 from "../attribution/license/radix-ui-react-context.txt?url";
import __glob_166 from "../attribution/license/radix-ui-react-direction.txt?url";
import __glob_167 from "../attribution/license/radix-ui-react-dismissable-layer.txt?url";
import __glob_168 from "../attribution/license/radix-ui-react-focus-guards.txt?url";
import __glob_169 from "../attribution/license/radix-ui-react-focus-scope.txt?url";
import __glob_170 from "../attribution/license/radix-ui-react-id.txt?url";
import __glob_171 from "../attribution/license/radix-ui-react-popper.txt?url";
import __glob_172 from "../attribution/license/radix-ui-react-portal.txt?url";
import __glob_173 from "../attribution/license/radix-ui-react-presence.txt?url";
import __glob_174 from "../attribution/license/radix-ui-react-primitive.txt?url";
import __glob_175 from "../attribution/license/radix-ui-react-select.txt?url";
import __glob_176 from "../attribution/license/radix-ui-react-slot.txt?url";
import __glob_177 from "../attribution/license/radix-ui-react-use-callback-ref.txt?url";
import __glob_178 from "../attribution/license/radix-ui-react-use-controllable-state.txt?url";
import __glob_179 from "../attribution/license/radix-ui-react-use-effect-event.txt?url";
import __glob_180 from "../attribution/license/radix-ui-react-use-layout-effect.txt?url";
import __glob_181 from "../attribution/license/radix-ui-react-use-previous.txt?url";
import __glob_182 from "../attribution/license/radix-ui-react-use-rect.txt?url";
import __glob_183 from "../attribution/license/radix-ui-react-use-size.txt?url";
import __glob_184 from "../attribution/license/radix-ui-react-visually-hidden.txt?url";
import __glob_185 from "../attribution/license/radix-ui-rect.txt?url";
import __glob_186 from "../attribution/license/react-dom.txt?url";
import __glob_187 from "../attribution/license/react-number-format.txt?url";
import __glob_188 from "../attribution/license/react-remove-scroll-bar.txt?url";
import __glob_189 from "../attribution/license/react-remove-scroll.txt?url";
import __glob_190 from "../attribution/license/react-style-singleton.txt?url";
import __glob_191 from "../attribution/license/react.txt?url";
import __glob_192 from "../attribution/license/require-from-string.txt?url";
import __glob_193 from "../attribution/license/restructure.txt?url";
import __glob_194 from "../attribution/license/sax.txt?url";
import __glob_195 from "../attribution/license/scheduler.txt?url";
import __glob_196 from "../attribution/license/scure-base.txt?url";
import __glob_197 from "../attribution/license/smol-toml.txt?url";
import __glob_198 from "../attribution/license/sqlite.org-sqlite-wasm.txt?url";
import __glob_199 from "../attribution/license/strnum.txt?url";
import __glob_200 from "../attribution/license/style-mod.txt?url";
import __glob_201 from "../attribution/license/sucrase.txt?url";
import __glob_202 from "../attribution/license/swc-helpers.txt?url";
import __glob_203 from "../attribution/license/tabbable.txt?url";
import __glob_204 from "../attribution/license/tabler-icons-react.txt?url";
import __glob_205 from "../attribution/license/tabler-icons.txt?url";
import __glob_206 from "../attribution/license/tagged-tag.txt?url";
import __glob_207 from "../attribution/license/thenify-all.txt?url";
import __glob_208 from "../attribution/license/thenify.txt?url";
import __glob_209 from "../attribution/license/tiny-inflate.txt?url";
import __glob_210 from "../attribution/license/tinyglobby.txt?url";
import __glob_211 from "../attribution/license/ts-interface-checker.txt?url";
import __glob_212 from "../attribution/license/tslib.txt?url";
import __glob_213 from "../attribution/license/tweetnacl.txt?url";
import __glob_214 from "../attribution/license/type-fest.txt?url";
import __glob_215 from "../attribution/license/types-emscripten.txt?url";
import __glob_216 from "../attribution/license/types-react-dom.txt?url";
import __glob_217 from "../attribution/license/types-react.txt?url";
import __glob_218 from "../attribution/license/types-trusted-types.txt?url";
import __glob_219 from "../attribution/license/uiw-codemirror-extensions-basic-setup.txt?url";
import __glob_220 from "../attribution/license/uiw-react-codemirror.txt?url";
import __glob_221 from "../attribution/license/unicode-properties.txt?url";
import __glob_222 from "../attribution/license/unicode-trie.txt?url";
import __glob_223 from "../attribution/license/use-callback-ref.txt?url";
import __glob_224 from "../attribution/license/use-sidecar.txt?url";
import __glob_225 from "../attribution/license/utif2.txt?url";
import __glob_226 from "../attribution/license/vvo-tzdb.txt?url";
import __glob_227 from "../attribution/license/w3c-keyname.txt?url";
import __glob_228 from "../attribution/license/world-countries.txt?url";
import __glob_229 from "../attribution/license/ws.txt?url";
import __glob_230 from "../attribution/license/xml-naming.txt?url";
import __glob_231 from "../attribution/license/xmldoc.txt?url";
import __glob_232 from "../attribution/license/yaml.txt?url";
